import React, { useState, useEffect } from 'react';
import { Box, Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemButton, Typography } from '@mui/material';
import Loading from '../Loading';
import BreadCrumbs from '../BreadCrumbs';
import { Helmet } from 'react-helmet';
import SessionConstants from '../../config/SessionConstants.js';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import educateComponents from './educateComponents.js';
import educateMenu from './educateMenu.js';

export default function Educate() {
    const [breadCrumbs, setBreadCrumbs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [programId, setProgramId] = useState(null);
    const [selectedKey, setSelectedKey] = useState('welcome');
    const [expanded, setExpanded] = useState([]);

    useEffect(() => {
        const programId = sessionStorage.getItem(SessionConstants.PROGRAM_ID);
    
        setProgramId(programId);
        setBreadCrumbs([{ name: 'Educate' }]);
    }, []);

    
   useEffect(() => {
    const hash = window.location.hash.substring(1); // remove the '#' symbol
    if (hash) {
      handleSubItemClick(hash);
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);


    const handleAccordionChange = (panelIdx) => (event, isExpanded) => {
        const selectedInPanel = educateMenu[panelIdx].items.some(item => item.key === selectedKey);

        setExpanded(prev => {
            if (isExpanded) {
                // Expand this panel
                return prev.includes(panelIdx) ? prev : [...prev, panelIdx];
            } else {
                // Collapse only if not the selected panel
                if (selectedInPanel) return prev; // Prevent collapse
                return prev.filter(idx => idx !== panelIdx);
            }
        });
    };

    // When a sub-item is clicked, select it and ensure its parent is expanded
    const handleSubItemClick = (itemKey) => {
        setSelectedKey(itemKey);
        const parentIdx = educateMenu.findIndex(section =>
            section.items.some(item => item.key === itemKey)
        );
        setExpanded(prev =>
            prev.includes(parentIdx) ? prev : [...prev, parentIdx]
        );
    };

    return (
        <>
        <Loading isLoading={loading} />
        <Helmet>
            <title>ED3N - Educate</title>
        </Helmet>
        <BreadCrumbs breadCrumbs={breadCrumbs} />
        <h1 className="pageTitle">Educate</h1>
        <Box sx={{ 
            display: 'flex', 
            minHeight: '50vh',
            border: '1px solid rgba(0, 0, 0, 0.12)',
        }}>
            <Box sx={{
                width: '25%',
                minWidth: 220,
                bgcolor: 'background.paper',
                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            }}>
                {educateMenu.map((section, idx) => {
                    const selectedInPanel = section.items.some(item => item.key === selectedKey);
                    return (
                        <Accordion
                            key={section.label}
                            expanded={expanded.includes(idx)}
                            onChange={handleAccordionChange(idx)}
                            disableGutters
                            elevation={0}
                            sx={{
                                mb: 0,
                                borderRight: 'none',
                                boxShadow: 'none',
                                '&::before': {
                                display: 'none',
                                },
                            }}
                        >
                            <AccordionSummary
                                id={`educate-accordion-summary-${idx}`}
                                expandIcon={selectedInPanel ? null : <ExpandMoreIcon />}
                                sx={{
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                    bgcolor: selectedInPanel ? 'var(--quaternary-color)' : 'background.default',
                                    minHeight: 56,
                                    cursor: selectedInPanel ? 'default' : 'pointer',
                                    '&.Mui-expanded': {
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                    minHeight: 56,
                                    },
                                    '.MuiAccordionSummary-content': {
                                    margin: 0,
                                    },
                                    pointerEvents: selectedInPanel ? 'none' : 'auto',
                                }}
                                >
                                <Typography variant="body1">{section.label}</Typography>
                            </AccordionSummary>
                            <AccordionDetails 
                                aria-labelledby={`educate-accordion-summary-${idx}`}
                                sx={{
                                p: 0,
                                borderBottom: '1px solid rgba(0, 0, 0, 0.12)', 
                                }}
                            >
                                <List>
                                    {section.items.map(item => (
                                        <ListItem key={item.key} disablePadding>
                                        <ListItemButton
                                            selected={selectedKey === item.key}
                                            onClick={() => handleSubItemClick(item.key)}
                                            sx={{
                                            '&.Mui-selected': {
                                                backgroundColor: 'var(--tertiary-color)', 
                                                color: 'var(--primary-color)',
                                            },
                                            }}
                                        >
                                            <Typography variant="body2">{item.label}</Typography>
                                        </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>
            <Box sx={{ flex: 1, p: 4 }}>
                {educateComponents[selectedKey]}
            </Box>
        </Box>
        </>
    );
}